import React, { useMemo } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getPasswordRequirements } from 'utils/passwordValidation';
import { useTranslation } from 'react-i18next';
import { styles } from './PasswordStrengthIndicator.styles';

const PasswordStrengthIndicator = ({ password = '' }) => {
  const { i18n } = useTranslation();

  const passwordRequirements = useMemo(() => getPasswordRequirements(), [i18n.language]);

  const strength = useMemo(() => {
    let score = 0;
    passwordRequirements.forEach(requirement => {
      if (requirement.regex.test(password)) {
        score += 1;
      }
    });
    return (score / passwordRequirements.length) * 100;
  }, [password, passwordRequirements]);

  const getColor = strength => {
    if (strength === 100) return 'success';
    if (strength >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box sx={styles.container}>
      <LinearProgress variant="determinate" value={strength} color={getColor(strength)} sx={styles.progressBar} />
      <Box sx={styles.requirementsContainer}>
        {passwordRequirements.map((requirement, index) => (
          <Box
            key={index}
            sx={{
              ...styles.requirementItem,
              color: requirement.regex.test(password) ? 'success.main' : 'text.secondary'
            }}
          >
            {requirement.regex.test(password) ? (
              <CheckCircleIcon color="success" sx={{ fontSize: '1.1rem' }} />
            ) : (
              <CancelIcon color="disabled" sx={{ fontSize: '1.1rem' }} />
            )}
            <Typography variant="body1" sx={styles.requirementText}>
              {requirement.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
